import React, { useCallback, useEffect, useRef, useState } from 'react';
import { setVMTDataFromTransportation } from '../../services/vmtDataService';
import { supabaseClient } from '../../services/supabase';
import { useLocation } from '../../contexts/LocationContext';
import { useChartPngRegistryActions } from '../../contexts/ChartPngRegistryContext';
import { useAdminPageSettings } from '../../hooks/useAdminPageSettings';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from '../charts/ChartLayout';
import EVChargingInfrastructure from '../charts/EVChargingInfrastructure';
import EVFleetAdoption from '../charts/EVFleetAdoption';
import { KeyMetric, KeyMetrics } from '../charts/KeyMetrics';
import { VMTChart, VMTMetrics } from '../charts/VMTChart';
import { VMTComparisonChart } from '../charts/VMTComparisonChart';
import { EMFACCountyVMTChart } from '../charts/EMFACCountyVMTChart';
import { EMFACFleetComparisonChart } from '../charts/EMFACFleetComparisonChart';
import { PageHeader } from '../layout/PageHeader';
import EVAdoptionBarriersChart from '../charts/EVAdoptionBarriersChart';
import MeansOfTransportation from '../charts/MeansOfTransportation';
import ExtremeCommutingPieChart from '../charts/ExtremeCommutingPieChart';
import TransportationBurdenSection from '../charts/TransportationBurdenSection';
import GenericMapTiered from '../charts/GenericMapTiered';
import { CustomSectionsForPage } from '../customContent/CustomSectionsForPage';
import { CustomDatasetsForPage } from '../customContent/CustomDatasetsForPage';
import { DepartureTimeChart } from '../charts/DepartureTimeChart';


interface TransportationProps {
  location?: string;
}

interface TransportMapMetrics {
  currentValue: number;
  californiaAverage: number;
  year: number;
  variable: string;
  minValue?: number;
  maxValue?: number;
}

interface TransportationBurdenNarrative {
  locationBurdenPct: number | null;
  californiaBurdenPct: number | null;
  locationMonthlyCost: number | null;
  californiaMonthlyCost: number | null;
  year: number | null;
}

export const Transportation: React.FC<TransportationProps> = ({ location: propLocation }) => {
  const { selectedLocation, selectedGeofips } = useLocation();
  const [activeVMTView, setActiveVMTView] = useState<string>('overview');
  const [activeEVView, setActiveEVView] = useState<string>('overview');
  const [activeChargingView, setActiveChargingView] = useState<string>('overview');
  const [burdenChartView, setBurdenChartView] = useState<'burden' | 'cost'>('burden');
  const [vmtChartView, setVmtChartView] = useState<string>('total');
  const [vmtMetrics, setVmtMetrics] = useState<VMTMetrics | null>(null);
  // VMT per capita metrics removed - no longer needed
  const [evOverview, setEvOverview] = useState<{ observedYear: number; observedTotal: number; projected2030: number; target2030: number; onPace: boolean } | null>(null);
  const [evChargingView, setEvChargingView] = useState<'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map'>('peoplePerCharger');
  const [evAdoptionView, setEvAdoptionView] = useState<'perCapita' | 'projection' | 'map'>('projection');
  const [vmtDataTableContent, setVmtDataTableContent] = useState<React.ReactNode>(null);
  const [vmtShowDataTable, setVmtShowDataTable] = useState(false);
  const [vmtDownloadPNG, setVmtDownloadPNG] = useState<(() => void) | null>(null);
  const [vmtDownloadCSV, setVmtDownloadCSV] = useState<(() => void) | null>(null);
  const [comparisonDownloadPNG, setComparisonDownloadPNG] = useState<(() => void) | null>(null);
  const [emfacCountyDownloadPNG, setEmfacCountyDownloadPNG] = useState<(() => void) | null>(null);
  const [emfacFleetDownloadPNG, setEmfacFleetDownloadPNG] = useState<(() => void) | null>(null);
  const [transportMapMetrics, setTransportMapMetrics] = useState<TransportMapMetrics | null>(null);
  const [activeTransportMapView, setActiveTransportMapView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  const [topDepartureWindow, setTopDepartureWindow] = useState<string | null>(null);
  // State for VMT backcasting options
  const [vmt2005_2014Method, setVmt2005_2014Method] = useState<'EMFAC2025' | 'Population' | 'Combined'>('Combined');
  const [vmtBackcastingMethod, setVmtBackcastingMethod] = useState<'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Population' | 'Combined'>('Combined');
  const [commuteMetrics, setCommuteMetrics] = useState<{ medianMinutes: number | null; year: number | null; californiaMedian: number | null; extremeCommutePct: number | null }>({ medianMinutes: null, year: null, californiaMedian: null, extremeCommutePct: null });
  const [burdenNarrative, setBurdenNarrative] = useState<TransportationBurdenNarrative | null>(null);

  const vmtDownloadPNGRef = useRef<(() => void) | null>(null);
  const comparisonDownloadPNGRef = useRef<(() => void) | null>(null);
  const emfacCountyDownloadPNGRef = useRef<(() => void) | null>(null);
  const emfacFleetDownloadPNGRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    vmtDownloadPNGRef.current = vmtDownloadPNG;
  }, [vmtDownloadPNG]);
  useEffect(() => {
    comparisonDownloadPNGRef.current = comparisonDownloadPNG;
  }, [comparisonDownloadPNG]);
  useEffect(() => {
    emfacCountyDownloadPNGRef.current = emfacCountyDownloadPNG;
  }, [emfacCountyDownloadPNG]);
  useEffect(() => {
    emfacFleetDownloadPNGRef.current = emfacFleetDownloadPNG;
  }, [emfacFleetDownloadPNG]);

  const currentLocation = propLocation || selectedLocation;
  const { register: registerPng, unregister: unregisterPng } = useChartPngRegistryActions();

  // Register every figure (each chart view) so the download modal lists all of them.
  // Deferred so registration runs after first paint and does not block load; stable deps to avoid re-run loops.
  useEffect(() => {
    if (!currentLocation) return;
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      const loc = currentLocation.replace(/\s+/g, '_');
      const delay = 600;
      const unreg: string[] = [];

      // VMT (5 views) - each switches view then runs that view's export after delay (pageId so these only show on Transportation tab)
      registerPng('VMT_Total', 'Vehicle Miles Traveled (Total VMT)', () => {
      setVmtChartView('total');
      setTimeout(() => vmtDownloadPNGRef.current?.(), delay);
    }, 'transportation');
    unreg.push('VMT_Total');
    registerPng('VMT_PerCapita', 'Vehicle Miles Traveled (Per Capita)', () => {
      setVmtChartView('percapita');
      setTimeout(() => vmtDownloadPNGRef.current?.(), delay);
    }, 'transportation');
    unreg.push('VMT_PerCapita');
    registerPng('VMT_Comparison', 'VMT Comparison (Light-Duty vs Heavy-Duty)', () => {
      setVmtChartView('comparison');
      setTimeout(() => comparisonDownloadPNGRef.current?.(), delay);
    }, 'transportation');
    unreg.push('VMT_Comparison');
    // EV Adoption (3 views) - switch view then export canvas
    const evAdoptionViews = [
      { id: 'EV_Adoption_Projection', label: 'Electric Vehicle Adoption (Projection to 2030)', view: 'projection' as const },
      { id: 'EV_Adoption_PerCapita', label: 'Electric Vehicle Adoption (Per 1,000 residents)', view: 'perCapita' as const },
      { id: 'EV_Adoption_Map', label: 'Electric Vehicle Adoption (Tract Map)', view: 'map' as const }
    ];
    evAdoptionViews.forEach(({ id, label, view }) => {
      registerPng(id, label, () => {
        setEvAdoptionView(view);
        setTimeout(() => {
          const canvas = document.querySelector('#ev-adoption-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            link.download = `EV_Adoption_${view === 'projection' ? 'Projection' : view === 'perCapita' ? 'Per_Capita' : 'Tract_Map'}_${loc}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }, delay);
      }, 'transportation');
      unreg.push(id);
    });

    // EV Charging (4 views)
    const evChargingViews = [
      { id: 'EV_Charging_People', label: 'EV Charging (People per Charger)', view: 'peoplePerCharger' as const },
      { id: 'EV_Charging_Counts', label: 'EV Charging (Total Chargers)', view: 'counts' as const },
      { id: 'EV_Charging_EVs', label: 'EV Charging (EVs per Charger)', view: 'evsPerCharger' as const },
      { id: 'EV_Charging_Map', label: 'EV Charging (Tract Map)', view: 'map' as const }
    ];
    evChargingViews.forEach(({ id, label, view }) => {
      registerPng(id, label, () => {
        setEvChargingView(view);
        setTimeout(() => {
          const canvas = document.querySelector('#ev-charging-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = view === 'counts' ? 'Total_Chargers' : view === 'peoplePerCharger' ? 'People_Per_Charger' : view === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map';
            link.download = `EV_Charging_${viewLabel}_${loc}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }, delay);
      }, 'transportation');
      unreg.push(id);
    });

    // Transportation-Related Maps (1 - current map variable)
    registerPng('Transportation_Maps', 'Transportation-Related Maps', () => {
      const container = document.querySelector('#transportation-maps-chart');
      const canvas = container?.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.download = `Transportation_Maps_${loc}.png`;
        link.href = (canvas as HTMLCanvasElement).toDataURL('image/png');
        link.click();
      }
    }, 'transportation');
        unreg.push('Transportation_Maps');
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(t);
      // Unregister all in case we unmount before timeout runs
      ['VMT_Total', 'VMT_PerCapita', 'VMT_Comparison',
       'EV_Adoption_Projection', 'EV_Adoption_PerCapita', 'EV_Adoption_Map',
       'EV_Charging_People', 'EV_Charging_Counts', 'EV_Charging_EVs', 'EV_Charging_Map',
       'Transportation_Maps'].forEach((id) => unregisterPng(id));
    };
  }, [currentLocation, registerPng, unregisterPng]);
  const {
    isSectionEnabled,
    isChartEnabled,
    getSectionOrder,
    getChartOrder
  } = useAdminPageSettings('transportation', currentLocation);

  const vmtOptions = [
    { id: 'vehicle_miles', value: 'total', label: 'Total VMT' },
    { id: 'vmt_percapita', value: 'percapita', label: 'Per Capita' },
    { id: 'vmt_comparison', value: 'comparison', label: 'VMT Comparison' }
  ];
  const allowedVmtOptions = vmtOptions
    .filter((option) => isChartEnabled('vmt', option.id))
    .sort((a, b) => getChartOrder('vmt', a.id) - getChartOrder('vmt', b.id));

  const evAdoptionOptions = [
    { id: 'ev_fleet_adoption', value: 'projection', label: 'Projection to 2030' },
    { id: 'ev_per_capita', value: 'perCapita', label: 'Per 1,000 residents' },
    { id: 'ev_map', value: 'map', label: 'Tract Map' }
  ];
  const allowedEvAdoptionOptions = evAdoptionOptions
    .filter((option) => isChartEnabled('ev_adoption', option.id))
    .sort((a, b) => getChartOrder('ev_adoption', a.id) - getChartOrder('ev_adoption', b.id));

  const evChargingOptions = [
    { id: 'ev_charging_people', value: 'peoplePerCharger', label: 'People per Charger' },
    { id: 'ev_charging_counts', value: 'counts', label: 'Total Chargers' },
    { id: 'ev_charging_evs', value: 'evsPerCharger', label: 'EVs per Charger' },
    { id: 'ev_charging_map', value: 'map', label: 'Tract Map' }
  ];
  const allowedEvChargingOptions = evChargingOptions
    .filter((option) => isChartEnabled('ev_charging', option.id))
    .sort((a, b) => getChartOrder('ev_charging', a.id) - getChartOrder('ev_charging', b.id));

  const transportMapVariables = [
    { id: 'ev_adoption', value: 'ev_adoption' },
    { id: 'total_chargers', value: 'total_chargers' },
    { id: 'vehicles_per_household', value: 'vehicles_per_household' },
    { id: 'zero_vehicle_households', value: 'zero_vehicle_households' },
    { id: 'commute_time', value: 'commute_time' }
  ];
  const allowedTransportMapVariables = transportMapVariables
    .filter((option) => isChartEnabled('transportation_maps', option.id))
    .sort((a, b) => getChartOrder('transportation_maps', a.id) - getChartOrder('transportation_maps', b.id))
    .map((option) => option.value);
  const transportMapsEnabled =
    isSectionEnabled('transportation_maps') && allowedTransportMapVariables.length > 0;


  React.useEffect(() => {
    if (allowedVmtOptions.length && !allowedVmtOptions.some((opt) => opt.value === vmtChartView)) {
      setVmtChartView(allowedVmtOptions[0].value);
    }
  }, [allowedVmtOptions, vmtChartView]);

  React.useEffect(() => {
    if (allowedEvAdoptionOptions.length && !allowedEvAdoptionOptions.some((opt) => opt.value === evAdoptionView)) {
      setEvAdoptionView(allowedEvAdoptionOptions[0].value as 'perCapita' | 'projection' | 'map');
    }
  }, [allowedEvAdoptionOptions, evAdoptionView]);

  React.useEffect(() => {
    if (allowedEvChargingOptions.length && !allowedEvChargingOptions.some((opt) => opt.value === evChargingView)) {
      setEvChargingView(allowedEvChargingOptions[0].value as 'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map');
    }
  }, [allowedEvChargingOptions, evChargingView]);

  const getSectionStyle = (sectionId: string) => ({
    order: getSectionOrder(sectionId),
    display: isSectionEnabled(sectionId) ? 'block' : 'none'
  });

  const toggleVMTView = (view: string) => {
    setActiveVMTView(view);
  };

  const toggleEVView = (view: string) => {
    setActiveEVView(view);
  };




  const handleVMTMetricsCalculated = useCallback((metrics: VMTMetrics) => {
    setVmtMetrics(metrics);
  }, []);

  // VMT per capita metrics handler removed - no longer needed

  const handleVMTDataCalculated = useCallback((data: any[]) => {
    console.log('🚗 Transportation page received VMT data:', data);
    console.log('🚗 VMT data length:', data.length);
    console.log('🚗 VMT data years:', data.map(d => d.year));
    if (!currentLocation) return;

    setVMTDataFromTransportation(data, currentLocation);

    if (typeof window !== 'undefined') {
      window.vmtChartData = data;
      console.log('🚗 VMT data set for GHG inventory (legacy):', data);
    }
  }, [currentLocation]);


  const handleTransportMapDataLoaded = useCallback((metrics: TransportMapMetrics) => {
    setTransportMapMetrics(metrics);
  }, []);

  // Load median commute time for key metrics from commute-time distribution.
  useEffect(() => {
    if (!currentLocation || !selectedGeofips) return;
    const load = async () => {
      try {
        const classifyBucket = (category: string): 'under15' | '15to30' | '30to45' | '45to60' | '60to90' | 'over90' | null => {
          const normalized = (category || '').toLowerCase().replace(/_/g, ' ');
          if (!normalized || normalized === 'total') return null;
          if (normalized.includes('90') || normalized.includes('or more')) return 'over90';
          if (normalized.includes('60')) return '60to90';
          if (normalized.includes('45')) return '45to60';
          if (normalized.includes('35') || normalized.includes('30')) return '30to45';
          if (normalized.includes('25') || normalized.includes('20') || normalized.includes('15')) return '15to30';
          if (normalized.includes('10') || normalized.includes('less') || normalized.includes('under') || normalized.includes('5')) return 'under15';
          return null;
        };

        const bucketBounds: Record<'under15' | '15to30' | '30to45' | '45to60' | '60to90' | 'over90', [number, number]> = {
          under15: [0, 15],
          '15to30': [15, 30],
          '30to45': [30, 45],
          '45to60': [45, 60],
          '60to90': [60, 90],
          over90: [90, 120]
        };

        const bucketOrder: Array<keyof typeof bucketBounds> = ['under15', '15to30', '30to45', '45to60', '60to90', 'over90'];

        const computeSummaryFromRows = (rows: Array<{ year: number; commute_time: string; estimate: number }>) => {
          if (!rows || rows.length === 0) return { median: null as number | null, year: null as number | null, extremePct: null as number | null };
          const latestYear = Math.max(...rows.map((r) => Number(r.year)).filter((y) => !Number.isNaN(y)));
          if (Number.isNaN(latestYear)) return { median: null, year: null, extremePct: null };

          const yearRows = rows.filter((r) => Number(r.year) === latestYear);
          const bucketCounts = new Map<keyof typeof bucketBounds, number>();
          bucketOrder.forEach((b) => bucketCounts.set(b, 0));

          yearRows.forEach((r) => {
            const bucket = classifyBucket(r.commute_time);
            if (!bucket) return;
            const val = Number(r.estimate) || 0;
            bucketCounts.set(bucket, (bucketCounts.get(bucket) || 0) + val);
          });

          const total = bucketOrder.reduce((sum, b) => sum + (bucketCounts.get(b) || 0), 0);
          if (total <= 0) return { median: null, year: latestYear, extremePct: null };
          const extremePct = ((bucketCounts.get('over90') || 0) / total) * 100;

          const midpoint = total / 2;
          let cumulative = 0;
          for (const bucket of bucketOrder) {
            const count = bucketCounts.get(bucket) || 0;
            const prev = cumulative;
            cumulative += count;
            if (count > 0 && midpoint <= cumulative) {
              const [lower, upper] = bucketBounds[bucket];
              const pctInBucket = (midpoint - prev) / count;
              const median = lower + pctInBucket * (upper - lower);
              return { median: Math.round(median * 10) / 10, year: latestYear, extremePct: Math.round(extremePct * 10) / 10 };
            }
          }

          return { median: null, year: latestYear, extremePct: Math.round(extremePct * 10) / 10 };
        };

        const fetchRowsForLocation = async (geofips: string) => {
          const { data: placeData, error: placeError } = await supabaseClient
            .from('extreme_commuting_by_place')
            .select('year, commute_time, estimate')
            .eq('place_geofips', geofips)
            .order('year', { ascending: false });
          if (!placeError && placeData?.length) return placeData as Array<{ year: number; commute_time: string; estimate: number }>;

          const { data: tractData, error: tractError } = await supabaseClient
            .from('extreme_commuting')
            .select('year, commute_time, estimate')
            .eq('geofips', geofips)
            .order('year', { ascending: false });
          if (tractError || !tractData?.length) return [];
          return tractData as Array<{ year: number; commute_time: string; estimate: number }>;
        };

        const [locRows, caRows] = await Promise.all([
          fetchRowsForLocation(String(selectedGeofips)),
          fetchRowsForLocation('6')
        ]);
        const locSummary = computeSummaryFromRows(locRows);
        const caSummary = computeSummaryFromRows(caRows);
        setCommuteMetrics({
          medianMinutes: locSummary.median,
          year: locSummary.year,
          californiaMedian: caSummary.median,
          extremeCommutePct: locSummary.extremePct
        });
      } catch {
        setCommuteMetrics({ medianMinutes: null, year: null, californiaMedian: null, extremeCommutePct: null });
      }
    };
    load();
  }, [currentLocation, selectedGeofips]);

  if (!currentLocation) {
    return (
      <div>
        <PageHeader 
          title="Transportation & Mobility" 
          subtitle="Please select a location to view transportation data."
        />
      </div>
    );
  }

  return (
    <div data-report-root="transportation">
      <PageHeader 
        title={`Transportation & Mobility - ${currentLocation}`}
        subtitle="Comprehensive transportation insights and mobility trends for the selected location."
        variant="transportation"
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      <div style={getSectionStyle('vmt')}>
      {/* Vehicle Miles Traveled Section */}
      <ChartLayout
        className="vmt-compact"
        title="Vehicle Miles Traveled (VMT)"
        chartType="bar"
        keyMetrics={
          <KeyMetrics className="key-metrics-four">
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.currentVMT}` : "Loading..."} 
              label="Average daily VMT per capita (2023)"
              color={(vmtMetrics ? vmtMetrics.currentVMT : 0) < (vmtMetrics ? vmtMetrics.californiaAverage : 0) ? "#10b981" : "#ef4444"}
            />
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.changeSince2020 > 0 ? '+' : ''}${vmtMetrics.changeSince2020}%` : "Loading..."} 
              label="Change since 2020"
              color={(vmtMetrics ? vmtMetrics.changeSince2020 : 0) < 0 ? "#10b981" : "#ef4444"}
            />
            <KeyMetric 
              value={vmtMetrics ? `${vmtMetrics.californiaAverage}` : "Loading..."} 
              label="California average VMT"
              color="#f59e0b"
            />
            <KeyMetric 
              value={vmtMetrics && vmtMetrics.pctVMTFromLightDuty != null ? `${vmtMetrics.pctVMTFromLightDuty}%` : "Loading..."} 
              label="VMT from Light-Duty"
              color="#1e3a8a"
            />
          </KeyMetrics>
        }
        chartContent={
          vmtChartView === 'comparison' ? (
            <VMTComparisonChart
              location={currentLocation}
              geofips={selectedGeofips}
              onDownloadPNGReady={(handler) => setComparisonDownloadPNG(() => handler)}
            />
          ) : (
            <VMTChart 
              location={currentLocation} 
              geofips={selectedGeofips || null} 
              chartView={vmtChartView as 'total' | 'percapita'} 
              emfac2005_2014Method={vmt2005_2014Method}
              backcastingMethod={vmtBackcastingMethod}
              emfacFleetVersion={vmtBackcastingMethod === 'EMFAC2021 Fleet' ? '2021' : '2025'}
              onMetricsCalculated={handleVMTMetricsCalculated} 
              onDataCalculated={handleVMTDataCalculated}
              onDataTableReady={(content, toggleFn) => {
                setVmtDataTableContent(content);
                // Store toggle function in a ref or state if needed
              }}
              onDownloadPNGReady={(handler) => setVmtDownloadPNG(() => handler)}
              onDownloadCSVReady={(handler) => setVmtDownloadCSV(() => handler)}
            />
          )
        }
        dataTableContent={vmtChartView !== 'comparison' ? vmtDataTableContent : undefined}
        showDataTable={vmtChartView !== 'comparison' ? vmtShowDataTable : false}
        onToggleDataTable={() => setVmtShowDataTable(!vmtShowDataTable)}
        onDownloadPNG={
          vmtChartView === 'comparison' ? comparisonDownloadPNG ?? undefined : vmtDownloadPNG ?? undefined
        }
        onDownloadCSV={vmtChartView !== 'comparison' && vmtDownloadCSV ? vmtDownloadCSV : undefined}
        pngFilename={`VMT_Chart_${vmtChartView === 'total' ? 'Total' : 'Per_Capita'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`VMT_Data_${vmtChartView === 'total' ? 'Total' : 'Per_Capita'}_${currentLocation.replace(/\s+/g, '_')}`}
        chartSelector={{
          options: allowedVmtOptions.map((option) => ({ value: option.value, label: option.label })),
          value: vmtChartView,
          onChange: setVmtChartView
        }}
        additionalButtonGroups={vmtChartView !== 'comparison' ? [
          {
            label: '(2005 - 2014)',
            options: [
              { value: 'Combined', label: 'Combined' },
              { value: 'EMFAC2025', label: 'EMFAC2025' },
              { value: 'Population', label: 'Population' }
            ],
            value: vmt2005_2014Method,
            onChange: (value) => setVmt2005_2014Method(value as 'EMFAC2025' | 'Population' | 'Combined')
          },
          {
            label: '(2015-2022)',
            options: [
              { value: 'Combined', label: 'Combined' },
              { value: 'AADT', label: 'AADT' },
              { value: 'EMFAC2025', label: 'EMFAC2025' },
              { value: 'EMFAC Fleet', label: 'EMFAC2025 Fleet' },
              { value: 'EMFAC2021 Fleet', label: 'EMFAC2021 Fleet' },
              { value: 'Population', label: 'Population' }
            ],
            value: vmtBackcastingMethod,
            onChange: (value) => setVmtBackcastingMethod(value as 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Population' | 'Combined')
          }
        ] : undefined}
        overviewContent={
          <OverviewContent
            title="VMT Overview"
            description="Vehicle Miles Traveled (VMT) captures the total distance traveled by all vehicles in a community over a given period of time, serving as a key measure of driving activity. This section offers three views: Total VMT, Per Capita VMT, and a comparison of light-duty versus heavy-duty vehicle miles. Statewide, VMT increased slightly year over year before the COVID-19 pandemic; stay-at-home orders and shifts to remote work then reduced VMT. Since then, VMT has been rising again, although not quite reaching pre-pandemic peak levels."
          >
              <p>
                {vmtMetrics ? (
                  <>
                    Light-duty vehicle miles traveled (VMT) in {currentLocation} totalled {(vmtMetrics.totalVMT2023 / 1000000).toFixed(2)} million miles in 2023 according to Replica's statewide origin-destination model.{' '}
                    This represents a {vmtMetrics.changeSince2020 > 0 ? 'increase' : 'decrease'} of {Math.abs(vmtMetrics.changeSince2020)}% since 2020, based on changes in local traffic counts, vehicle registrations, and population.{' '}
                    {vmtMetrics.changeSince2020 < 0
                      ? 'This reduction may reflect successful transportation demand management strategies, shifts toward more sustainable mobility options, and/or a general decline in transportation needs due to post-pandemic work-from-home allowances. '
                      : 'This increase indicates a rise in vehicle travel demand, possibly due to population growth, changes in work patterns, or a general need to invest in public transport infrastructure and normalize its use. '}
                    The current VMT per capita is {vmtMetrics.currentVMT > vmtMetrics.californiaAverage ? 'above' : 'below'} the California average of {vmtMetrics.californiaAverage} miles per capita
                    {vmtMetrics.currentVMT > vmtMetrics.californiaAverage
                      ? ', indicating a need for targeted policies to address rising transportation demands.'
                      : '.'}
                  </>
                ) : (
                  `Loading VMT data for ${currentLocation}...`
                )}
              </p>
              <p>
                The <strong>Total VMT</strong> and <strong>Per Capita</strong> tabs show trends over time from 2005 through 2023, combining backcasted historical data with Replica's recent estimates. The <strong>VMT Comparison</strong> tab reveals the relative contributions of light-duty passenger vehicles versus heavy-duty commercial vehicles to total VMT in {currentLocation}. Understanding this split is important for targeting emission reduction strategies: heavy-duty vehicles typically have higher emissions per mile but account for a smaller share of total miles traveled than light-duty vehicles.
              </p>
            </OverviewContent>
        }
        relevanceContent={
            <RelevanceContent
              title="Policy Relevance"
              description="Vehicle miles traveled (VMT) are the primary activity indicator for the transportation sector. Light-duty motor vehicles contribute nearly 40% of statewide emissions."
            >
              <div className="methodology-details-simple">
                <h5>Climate Action Planning</h5>
                <p>Local governments seek to limit VMT through housing densification, land use policies, public transit, biking infrastructure, micromobility, and other means. Despite these efforts, total VMT is on the rise statewide (following COVID).</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Quality of Life Indicators</h5>
                <p>Reduced VMT may indicate that people are able to access their daily needs without traveling as far, or by active mobility or transit options, which are indicators of better quality of life.</p>
              </div>
            </RelevanceContent>
        }
        equityContent={
            <EquityContent
              title="Equity Considerations"
              description={"Younger and low-income residents are less likely to own motor vehicles and contribute less to vehicle miles traveled, on average. However, many low-income communities exist in close proximity to highways and major roads, disproportionately burdening them with the environmental and health impacts of motor vehicle travel."}
            >
              <div className="methodology-details-simple">
                <h5>Access and Distance Challenges</h5>
                <p>Higher housing prices in urban centers relative to suburban or exurban areas leads many working adults to travel long distances to reach workplaces, shopping, and other destinations. This often disproportionately burdens low-income communities with longer motor vehicle travel times. Housing affordability and accessibility, especially in high demand, job-rich areas, is a key factor in reducing VMT.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Community Design Impact</h5>
                <p>Higher VMT can also indicate a lack of amenities nearby. Designing mixed-use communities with jobs, schools, shopping, and recreation nearby can reduce VMT by shifting vehicle use to shorter, local trips or encourage active transportation options like walking and biking.</p>
              </div>
            </EquityContent>
        }
        methodologyContent={
          vmtChartView === 'comparison' ? (
            <MethodologyContent
              title="Methodology"
              description="VMT Comparison chart shows light-duty and heavy-duty vehicle miles traveled separately, using city-specific data from emfac2025_fleet for recent years and EMFAC2025 backcasting for historical data."
              dataSources={[
                'emfac2025_fleet (city-specific EMFAC2025 data)',
                'emfac2021_county_filtered (county-level backcasting ratios)',
                'VMT chart data (for LDV consistency)'
              ]}
              methodology={[
                'Light-Duty Vehicle (LDV) VMT: Uses the same calculation as the main VMT chart, including all backcasting logic, to ensure consistency across the application.',
                'Heavy-Duty Vehicle (HDV) VMT for 2015-2023: Sums all total_vmt values from emfac2025_fleet where class=HDV for the selected location (city geofips), providing city-specific HDV data.',
                'Heavy-Duty Vehicle (HDV) VMT for 2005-2014: Uses county-level backcasting ratios from emfac2021_county_filtered, where the 2015 HDV VMT baseline is scaled by pre-calculated backcasting_ratio values for each year.',
                'HDV backcasting applies the county ratio to city data, ensuring historical trends match broader regional patterns while maintaining city-specific values for the baseline year (2015).',
                'This approach captures the fact that HDV VMT data is only available at city-level from 2015 onwards in emfac2025_fleet, requiring backcasting for earlier years.'
              ]}
            />
          ) : (
            <MethodologyContent
              title="1.1 VMT (Total / Per Capita) – Methods"
              description="VMT chart combines multiple data sources to estimate vehicle miles traveled (VMT) from 2005–2023, using EMFAC backcasting for historical data, AADT scaling for recent years, and Replica simulation for current data."
              dataSources={[
                'EMFAC2025 / EMFAC2021 (county and fleet)',
                'CARB Fleet Database',
                'CalTrans (AADT)',
                'Replica',
                'US Census (Population)'
              ]}
              methodology={[
                'Backcasting refers to estimating earlier-year values by applying year-specific scaling factors to a baseline year. Backcasting from 2005 to 2014 considers the change in county-level VMT using the California Air Resources Board\'s EMFAC2021 model converted to a scaling factor.',
                'Backcasting from 2015 to 2022 considers changes in vehicle ownership from the CARB Fleet Database, in addition to changes in local traffic counts, Average Annual Daily Traffic (AADT) from CalTrans.',
                'The CARB Fleet database covers registered vehicles in the selected geography and is used to quantify changes in vehicle ownership over time.',
                'CalTrans AADT monitors are used to quantify changes in local traffic counts over time. Together, these data sets measure local changes in inbound, outbound, and local traffic. This measurement approach more accurately captures real-world changes in local VMT than modeled approaches, which do not reliably provide accurate and consistent data over time.',
                'VMT for 2023 is from Replica\'s origin-destination travel demand model.'
              ]}
            />
          )
        }
        activeView={activeVMTView}
        onViewChange={toggleVMTView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        overviewSectionId="transportation"
        overviewChartId="vehicle_miles"
        registerPng={false}
      />
      </div>

      {/* Departure Time to Work */}
      <div style={getSectionStyle('departure_time')}>
        <ChartLayout
          title="Departure Time to Work"
          chartType="bar"
          keyMetrics={
            <KeyMetrics>
              <KeyMetric
                value={topDepartureWindow || '–'}
                label="Most common departure window"
                color="#2563eb"
              />
            </KeyMetrics>
          }
          chartContent={<DepartureTimeChart location={currentLocation} onTopWindowChange={setTopDepartureWindow} />}
          overviewContent={
            <OverviewContent
              title="Departure Time Overview"
              description="Departure time to work shows when residents leave for work, which affects congestion patterns, transit demand, and transportation emissions."
            >
              <p>
                Earlier departure times can indicate long commutes or shift work, while later departures may reflect flexible schedules or closer jobs.
                Understanding this pattern helps design transit service, manage peak congestion, and align climate policies with real travel behavior.
              </p>
            </OverviewContent>
          }
          relevanceContent={
            <RelevanceContent
              title="Relevance to Climate Policy"
              description="Departure time patterns shape peak-period congestion and emissions."
            >
              <p>
                When many workers travel during the same peak hours, congestion and per‑mile emissions rise. Policies that support flexible work hours,
                telecommuting, and high‑frequency transit during peak periods can reduce congestion and emissions while maintaining access to jobs.
              </p>
            </RelevanceContent>
          }
          equityContent={
            <EquityContent
              title="Equity Considerations"
              description="Not all workers can shift their schedules or choose nearby jobs."
            >
              <p>
                Low‑wage workers and shift workers are more likely to have rigid schedules and early or late departure times, often with fewer transit options.
                Climate and transportation policies should consider whether early‑morning and late‑night service is available for these workers and whether
                housing near jobs is affordable.
              </p>
            </EquityContent>
          }
          methodologyContent={
            <MethodologyContent
              title="Data Sources and Methodology"
              description="Departure time metrics use ACS 5‑Year tract data aggregated to place and county."
              dataSources={[
                'ACS 5‑Year (Journey to Work – Departure Time)',
                'departure_time_to_work table (aggregated from tract‑level ACS data)'
              ]}
              methodology={[
                'Tract‑level ACS 5‑Year estimates of workers by departure time window are aggregated to place and county geographies using Census relationship files.',
                'For each geography and year, the number of workers departing in each time window is summed and, when not already present, converted to a percent of total workers.',
                'Charts show the distribution of workers by departure window for the most recent year with complete data.'
              ]}
            />
          }
          activeView={activeVMTView}
          onViewChange={toggleVMTView}
          showRelevanceEquity={true}
          enableComments={true}
          location={currentLocation}
          overviewSectionId="transportation"
          overviewChartId="departure_time"
        />
      </div>
      {/* Means of Transportation to Work */}
      <div style={getSectionStyle('means_of_transportation')}>
        <MeansOfTransportation 
          location={currentLocation}
          geofips={selectedGeofips}
        />
      </div>

      {/* Commute Times */}
      <div style={getSectionStyle('commute_time_extreme')}>
        <ChartLayout
          className="commute-extreme-compact"
          title="Commute Times"
          chartType="pie"
          keyMetrics={
            <KeyMetrics>
              <KeyMetric
                value={commuteMetrics.medianMinutes != null ? `${commuteMetrics.medianMinutes.toFixed(1)} min` : '—'}
                label={`${currentLocation} median commute time (2023)`}
                color="#1e3a8a"
              />
              <KeyMetric
                value={commuteMetrics.californiaMedian != null ? `${commuteMetrics.californiaMedian.toFixed(1)} min` : '—'}
                label="California median commute time (2023)"
                color="#f59e0b"
              />
              <KeyMetric
                value={commuteMetrics.extremeCommutePct != null ? `${commuteMetrics.extremeCommutePct.toFixed(1)}%` : '—'}
                label="Extreme Commutes (%)"
                color="#ef4444"
              />
            </KeyMetrics>
          }
          chartContent={<ExtremeCommutingPieChart location={currentLocation} />}
          overviewContent={
            <OverviewContent
              title="Commute Times"
              description="Average commute time and the share of workers with long (90+ minute one-way) commutes help identify jobs-housing balance and transportation burden."
            >
              <p>
                {commuteMetrics.medianMinutes != null ? (
                  <>
                    In {currentLocation}, the median one-way commute to work was {commuteMetrics.medianMinutes.toFixed(1)} minutes
                    {commuteMetrics.year ? ` (${commuteMetrics.year})` : ''}.
                    {commuteMetrics.californiaMedian != null && (
                      <> That is {commuteMetrics.medianMinutes > commuteMetrics.californiaMedian ? 'above' : 'below'} the California median of {commuteMetrics.californiaMedian.toFixed(1)} minutes.</>
                    )}
                    {' '}The pie chart shows how workers are distributed across commute time brackets; the “90+ min (extreme)” slice is the share of workers with commutes of 90 minutes or more one-way.
                  </>
                ) : (
                  `Commute time data for ${currentLocation} comes from the American Community Survey (ACS) and is allocated from census tracts to places using block group–place relationship files.`
                )}
              </p>
            </OverviewContent>
          }
          relevanceContent={
            <RelevanceContent
              title="Policy relevance"
              description="Commute times and extreme commuting matter for climate, equity, and quality of life."
            >
              <div className="methodology-details-simple">
                <h5>VMT and emissions</h5>
                <p>Longer commutes mean more vehicle miles traveled and higher transportation emissions. Reducing extreme commuting supports climate goals.</p>
              </div>
              <div className="methodology-details-simple">
                <h5>Jobs–housing balance</h5>
                <p>High shares of extreme commuters often indicate a mismatch between where people live and where jobs are, and can point to housing and transit investments.</p>
              </div>
            </RelevanceContent>
          }
          equityContent={
            <EquityContent
              title="Equity considerations"
              description="Long and extreme commutes often fall disproportionately on lower-income and minority workers."
            >
              <div className="methodology-details-simple">
                <p>Lower-income households are more likely to live farther from job centers due to housing costs, leading to longer commutes. Tracking extreme commuting by place helps target transit and housing policies.</p>
              </div>
            </EquityContent>
          }
          methodologyContent={
            <MethodologyContent
              title="Data and methodology"
              description="Tract-level ACS data is allocated to places using block group–place relationship files."
              dataSources={[
                'acs5_commute_time (tract-level mean travel time to work, ACS Table B08303)',
                'extreme_commuting (tract-level workers by commute time, ACS Table B08012)',
                'relationship_bgrp_place_10 and relationship_bgrp_place_20 (tract derived as bg_geofips/10)'
              ]}
              methodology={[
                'Average commute time: from pre-aggregated commute_time table (tract → place via relationship_bgrp_place, see populate_commute_time_from_acs5.sql).',
                'Extreme commuting pie: tract-level extreme_commuting is allocated to places using relationship_bgrp_place_20 (years ≥ 2019) and relationship_bgrp_place_10 (years &lt; 2019), with tract = bg_geofips/10. See docs/TRACT_OR_BLOCKGROUP_TO_PLACE_COUNTY_GUIDE.md.'
              ]}
            />
          }
          activeView={activeVMTView}
          onViewChange={toggleVMTView}
          showRelevanceEquity={true}
          enableComments={true}
          location={currentLocation}
          registerPng={false}
        />
      </div>

      {/* Transportation Burden */}
      <div style={getSectionStyle('transportation_burden')}>
        <ChartLayout
          title="Transportation Burden"
          chartType="bar"
          chartSelector={{
            options: [
              { value: 'burden', label: 'Income Spent on Transport (%)' },
              { value: 'cost', label: 'Monthly Cost ($)' }
            ],
            value: burdenChartView,
            onChange: (value: string) => setBurdenChartView(value as 'burden' | 'cost')
          }}
          chartContent={
            <TransportationBurdenSection
              location={currentLocation}
              chartView={burdenChartView}
              onSummaryCalculated={setBurdenNarrative}
            />
          }
          overviewContent={
            <OverviewContent
              title="Transportation Burden"
              description="Transportation burden reflects the share of household income spent on transportation, including vehicle ownership, fuel, and transit. Higher transportation costs are often associated with longer travel distances, limited access to nearby jobs and services, and greater reliance on driving. As a result, transportation burden serves as an indicator of how well land use, housing, and mobility systems are aligned."
            >
              <p>
                {burdenNarrative?.year != null && burdenNarrative.locationBurdenPct != null ? (
                  <>
                    In {burdenNarrative.year}, average transportation burden in {currentLocation} was {burdenNarrative.locationBurdenPct.toFixed(1)}% of household income
                    {burdenNarrative.californiaBurdenPct != null
                      ? `, ${burdenNarrative.locationBurdenPct > burdenNarrative.californiaBurdenPct ? 'above' : 'below'} the California average of ${burdenNarrative.californiaBurdenPct.toFixed(1)}%.`
                      : '.'}
                    {' '}
                    Average monthly transportation cost was
                    {burdenNarrative.locationMonthlyCost != null ? ` $${burdenNarrative.locationMonthlyCost.toFixed(0)}` : ' not available'}
                    {burdenNarrative.californiaMonthlyCost != null && burdenNarrative.locationMonthlyCost != null
                      ? `, compared with California at $${burdenNarrative.californiaMonthlyCost.toFixed(0)}.`
                      : '.'}
                  </>
                ) : (
                  <>
                    Tract-level burden is computed as monthly transportation cost divided by monthly income, then aggregated to {currentLocation} and compared with California. Higher burden indicates that households in the area spend a larger share of income on transportation, which is an equity concern especially in lower-income communities.
                  </>
                )}
              </p>
            </OverviewContent>
          }
          relevanceContent={
            <RelevanceContent
              title="Policy relevance"
              description="Transportation burden is directly influenced by local planning and investment decisions. Policies that support compact, mixed-use development, expand access to reliable public transit, and improve walking and biking infrastructure can reduce both travel demand and household costs. Lowering transportation burden not only improves affordability but also helps reduce vehicle miles traveled (VMT) and associated greenhouse gas emissions."
            >
              <div className="methodology-details-simple">
                <p>Reducing transportation burden supports climate and affordability goals by lowering household travel costs while also reducing vehicle miles traveled and associated greenhouse gas emissions.</p>
              </div>
            </RelevanceContent>
          }
          equityContent={
            <EquityContent
              title="Equity considerations"
              description="Transportation costs disproportionately impact lower-income households, who often spend a larger share of their income on travel. Residents in areas with limited transit access or far from job centers may face higher costs due to longer commutes and greater dependence on personal vehicles. Reducing transportation burden can improve access to jobs, services, and opportunities, while advancing more equitable and inclusive community development."
            >
              <div className="methodology-details-simple">
                <p>Tracking transportation burden by place helps identify communities where travel costs consume a large share of income, informing targeted investments in transit, housing, and safer active transportation options.</p>
              </div>
            </EquityContent>
          }
          methodologyContent={
            <MethodologyContent
              title="Data and methodology"
              description="Transportation burden is produced by the Python pipeline in the repo."
              dataSources={[
                'python/transportation_burden.py (db.transportation_burden.csv)',
                'Replica VMT, GasBuddy gas prices, ACS median income by tract'
              ]}
              methodology={[
                'Monthly VMT per capita from Replica is combined with county gas prices and tract-level median income to compute monthly cost and burden (cost / monthly_income).',
                'Output table: transportation_burden (geofips, county, year, month, monthly_vmt, gas_price, fuel_economy, total_cost, burden).'
              ]}
            />
          }
          activeView={activeVMTView}
          onViewChange={toggleVMTView}
          showRelevanceEquity={true}
          enableComments={true}
          location={currentLocation}
          registerPng={false}
        />
      </div>

      {/* Electric Vehicle Adoption Section */}
      <div style={getSectionStyle('ev_adoption')}>
      <ChartLayout
        title="Electric Vehicle Adoption"
        chartType="line"
        chartSelector={{
          options: allowedEvAdoptionOptions.map((option) => ({ value: option.value, label: option.label })),
          value: evAdoptionView,
          onChange: (value: string) => setEvAdoptionView(value as 'perCapita' | 'projection' | 'map')
        }}
        keyMetrics={
          <KeyMetrics>
            <KeyMetric 
              value="8.7" 
              label="EVs per 1,000 residents"
              color="#1e3a8a"
            />
            <KeyMetric 
              value="+156%" 
              label="Growth since 2020"
              color="#10b981"
            />
            <KeyMetric 
              value="127" 
              label="Public charging stations"
              color="#f59e0b"
            />
          </KeyMetrics>
        }
        chartContent={
          evAdoptionView === 'map' ? (
            <GenericMapTiered 
              location={currentLocation}
              hideVariableSelector={true}
              lockedVariable="ev_adoption"
              onDataLoaded={(metrics) => {
                // Update key metrics if needed when map data loads
                if (metrics && metrics.variable === 'ev_adoption') {
                  // Could update evOverview with map-derived metrics if needed
                }
              }}
            />
          ) : (
            <div id="ev-adoption-chart">
              <EVFleetAdoption 
                location={currentLocation} 
                geofips={selectedGeofips} 
                activeTab={evAdoptionView}
                onTabChange={setEvAdoptionView}
                onMetricsCalculated={setEvOverview} 
              />
            </div>
          )
        }
        overviewContent={
          <OverviewContent
            title="EV Adoption Overview"
            description="This chart displays electric vehicle adoption trends, a critical indicator of transportation electrification progress."
          >
            <p>
              {evOverview ? (
                <>
                  In {evOverview.observedYear}, {currentLocation} had {evOverview.observedTotal.toLocaleString()} light-duty electric vehicles and is on pace to reach {Math.round(evOverview.projected2030).toLocaleString()} by 2030. To meet California's target of 5 million electric vehicles by 2030, {currentLocation} would need approximately {Math.round(evOverview.target2030).toLocaleString()} vehicles. {currentLocation} {evOverview.onPace ? 'will surpass this target if growth continues at the current rate.' : 'is not yet on pace and will need to accelerate adoption to meet the target.'}
                </>
              ) : (
                `Loading EV adoption overview for ${currentLocation}...`
              )}
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Electric vehicle adoption is essential for reducing transportation emissions and achieving climate goals."
          >
            <div className="methodology-details-simple">
              <h5>Emissions Reduction</h5>
              <p>EVs produce significantly fewer emissions than gasoline vehicles, especially when powered by renewable electricity.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Infrastructure Planning</h5>
              <p>EV adoption rates guide charging infrastructure investment and grid capacity planning for transportation electrification.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Access to EVs remains uneven across income, race, and geography. Equity-centered programs are necessary to ensure all Californians benefit."
          >
            <div className="methodology-details-simple">
              <h5>Affordability & Tenure</h5>
              <p>Lower-income households and renters face high upfront costs and limited home charging. Used-EV rebates and low‑income financing expand access.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Charging for Renters</h5>
              <p>Mandates and incentives for multifamily and renter access to charging reduce inequities in home charging availability.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Targeted Community Grants</h5>
              <p>Place‑based grants and community charging projects support adoption in underserved neighborhoods.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Data source and roll‑up approach for EV adoption metrics."
            dataSources={[
              'EMFAC Fleet Database (CARB) built from DMV vehicle registrations',
              'Population data for per‑capita calculations'
            ]}
            methodology={[
              'We query EV fleet (fuel = Electric and PluginHybrid, class = LDV) at tract level rolled up to cities/counties using a population‑weighted correspondence table.',
              'Per‑capita chart shows EVs per 1,000 residents; statewide (geofips=6) shown for comparison.',
              'Projections, where shown, consider the 5‑year average annual growth in EV registrations.'
            ]}
          />
        }
        activeView={activeEVView}
        onViewChange={toggleEVView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        overviewSectionId="transportation"
        overviewChartId="ev_fleet_adoption"
        onDownloadPNG={() => {
          if (evAdoptionView === 'map') {
            // Map download would need to be handled by GenericMapTiered component
            console.log('Map PNG download - to be implemented in GenericMapTiered');
            return;
          }
          const canvas = document.querySelector('#ev-adoption-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evAdoptionView === 'perCapita' ? 'Per_Capita' : 'Projection';
            link.download = `EV_Adoption_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // This would need to be implemented based on the chart data
          const viewLabel = evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection';
          console.log(`CSV download for EV Adoption ${viewLabel} not yet implemented`);
        }}
        pngFilename={`EV_Adoption_${evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Adoption_${evAdoptionView === 'perCapita' ? 'Per_Capita' : evAdoptionView === 'map' ? 'Tract_Map' : 'Projection'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
        registerPng={false}
      />

      {/* EV Adoption Barriers by Income (within Electric Vehicle Adoption section) */}
      <EVAdoptionBarriersChart location={currentLocation} />
      </div>

      {/* EV Charging Infrastructure Section */}
      <div style={getSectionStyle('ev_charging')}>
      <ChartLayout
        title="EV Charging Infrastructure"
        description="Current charging station infrastructure compared to 2030 targets and accessibility metrics."
        chartType="bar"
        chartContent={
          evChargingView === 'map' ? (
            <GenericMapTiered 
              location={currentLocation}
              hideVariableSelector={true}
              lockedVariable="total_chargers"
              onDataLoaded={(metrics) => {
                // Update metrics if needed when map data loads
                if (metrics && metrics.variable === 'total_chargers') {
                  // Could update charging metrics if needed
                }
              }}
            />
          ) : (
            <div id="ev-charging-chart">
              <EVChargingInfrastructure location={currentLocation} activeTab={evChargingView} onTabChange={setEvChargingView} />
            </div>
          )
        }
        chartSelector={{
          options: allowedEvChargingOptions.map((option) => ({ value: option.value, label: option.label })),
          value: evChargingView,
          onChange: (value: string) => setEvChargingView(value as 'counts' | 'peoplePerCharger' | 'evsPerCharger' | 'map')
        }}
        activeView={activeChargingView}
        onViewChange={setActiveChargingView}
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        layout="side-by-side"
        onDownloadPNG={() => {
          if (evChargingView === 'map') {
            // Map download would need to be handled by GenericMapTiered component
            console.log('Map PNG download - to be implemented in GenericMapTiered');
            return;
          }
          const canvas = document.querySelector('#ev-charging-chart canvas') as HTMLCanvasElement;
          if (canvas) {
            const link = document.createElement('a');
            const viewLabel = evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger';
            link.download = `EV_Charging_${viewLabel}_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL();
            link.click();
          }
        }}
        onDownloadCSV={() => {
          // This would need to be implemented based on the chart data
          const viewLabel = evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : 'EVs_Per_Charger';
          console.log(`CSV download for EV Charging ${viewLabel} not yet implemented`);
        }}
        pngFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : evChargingView === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map'}_${currentLocation.replace(/\s+/g, '_')}`}
        csvFilename={`EV_Charging_${evChargingView === 'counts' ? 'Total_Chargers' : evChargingView === 'peoplePerCharger' ? 'People_Per_Charger' : evChargingView === 'evsPerCharger' ? 'EVs_Per_Charger' : 'Tract_Map'}_Data_${currentLocation.replace(/\s+/g, '_')}`}
        registerPng={false}
        overviewContent={
          <OverviewContent
            title="EV Charging Infrastructure Overview"
            description="This chart compares current charging infrastructure to 2030 targets and highlights access and utilization patterns."
          >
            <p>
              Charging access is a key requirement for transportation electrification. This section summarizes the current charging network in {currentLocation} and compares local availability against statewide benchmarks.
            </p>
            <p>
              Metrics include total chargers, people per charger, and EVs per charger, capturing both infrastructure capacity and practical access. Together, these measures help identify coverage gaps and priority areas for equitable investment.
            </p>
          </OverviewContent>
        }
        relevanceContent={
          <RelevanceContent
            title="Policy Relevance"
            description="Why EV charging infrastructure matters for climate action and local planning."
          >
            <p>
              <strong>Policy Relevance:</strong> EV charging infrastructure is directly tied to California's climate goals 
              and transportation electrification policies. The state's commitment to reach carbon neutrality by 2045 
              requires a massive expansion of clean transportation infrastructure.
            </p>
            <p>
              <strong>Local Planning:</strong> Cities and counties need to understand their current charging infrastructure 
              status to plan for future growth, allocate resources effectively, and ensure equitable access to clean 
              transportation options for all residents.
            </p>
            <p>
              <strong>Economic Development:</strong> Charging infrastructure supports local economic development by 
              attracting EV drivers, supporting local businesses, and creating jobs in the clean energy sector.
            </p>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Ensuring equitable access to EV charging infrastructure across all communities."
          >
            <p>
              <strong>Accessibility Concerns:</strong> EV charging infrastructure must be distributed equitably to ensure 
              all communities can participate in the clean transportation transition. Historically underserved communities 
              may face barriers to EV adoption without adequate charging infrastructure.
            </p>
            <p>
              <strong>Multi-Family Housing:</strong> Residents of apartments and condominiums often lack access to home 
              charging, making public charging infrastructure even more critical for these communities.
            </p>
            <p>
              <strong>Rural and Underserved Areas:</strong> Ensuring charging infrastructure reaches rural communities and 
              areas with limited transportation options is essential for statewide equity in clean transportation access.
            </p>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Data Sources & Methodology"
            description="Detailed information about data sources and calculation methods for EV charging infrastructure analysis."
            dataSources={[
              'U.S. Department of Energy, Alternative Fuels Data Center (AFDC) Station Locator',
              'U.S. Census Bureau American Community Survey (ACS) 5-Year Estimates for 2023',
              'California Air Resources Board (CARB) EMFAC2021 model and CARB Fleet Database for 2023'
            ]}
            methodology={[
              'Charging station counts are aggregated by geographic location from AFDC database',
              'Data includes public and private charging stations with status: Available (E), Planned (P), and Temporarily Unavailable (T)',
              'Per capita calculations use 2023 population estimates from ACS',
              'EV counts include both Battery Electric Vehicles (BEVs) and Plug-in Hybrid Electric Vehicles (PHEVs)',
              'County-level data is used for comparison when city-level data is not available',
              '2030 targets based on California\'s goal to reach 1 million charging stations statewide'
            ]}
          />
        }
      />
      </div>

      {/* Transportation-Related Maps */}
      <div
        style={{
          order: getSectionOrder('transportation_maps'),
          display: transportMapsEnabled ? 'block' : 'none'
        }}
      >
      <ChartLayout
        title="Transportation-Related Maps"
        description="Interactive maps for transportation indicators such as EV adoption, charging infrastructure, and vehicle access."
        chartType="map"
        layout="vertical"
        keyMetrics={
          transportMapMetrics ? (
            <KeyMetrics>
              <KeyMetric
                value={
                  transportMapMetrics.variable === 'ev_adoption'
                    ? `${transportMapMetrics.currentValue.toFixed(1)} EVs/100`
                    : transportMapMetrics.variable === 'total_chargers'
                    ? `${Math.round(transportMapMetrics.currentValue).toLocaleString()} chargers`
                    : transportMapMetrics.variable === 'vehicles_per_household'
                    ? `${transportMapMetrics.currentValue.toFixed(2)} vehicles/HH`
                    : transportMapMetrics.variable === 'zero_vehicle_households'
                    ? `${Math.round(transportMapMetrics.currentValue).toLocaleString()} zero-vehicle HHs`
                    : transportMapMetrics.variable === 'commute_time'
                    ? `${transportMapMetrics.currentValue.toFixed(1)} min`
                    : `${Math.round(transportMapMetrics.currentValue).toLocaleString()}`
                }
                label={
                  transportMapMetrics.variable === 'ev_adoption'
                    ? 'EVs per 100 People'
                    : transportMapMetrics.variable === 'total_chargers'
                    ? 'Total EV Chargers'
                    : transportMapMetrics.variable === 'vehicles_per_household'
                    ? 'Vehicles per Household'
                    : transportMapMetrics.variable === 'zero_vehicle_households'
                    ? 'Zero Vehicle Households (est.)'
                    : transportMapMetrics.variable === 'commute_time'
                    ? 'Average Commute Time'
                    : 'Value'
                }
              />
              <KeyMetric
                value={
                  transportMapMetrics.variable === 'ev_adoption'
                    ? `${transportMapMetrics.californiaAverage.toFixed(1)} EVs/100`
                    : transportMapMetrics.variable === 'total_chargers'
                    ? `${Math.round(transportMapMetrics.californiaAverage).toLocaleString()} chargers`
                    : transportMapMetrics.variable === 'vehicles_per_household'
                    ? `${transportMapMetrics.californiaAverage.toFixed(2)} vehicles/HH`
                    : transportMapMetrics.variable === 'zero_vehicle_households'
                    ? `${Math.round(transportMapMetrics.californiaAverage).toLocaleString()} zero-vehicle HHs`
                    : transportMapMetrics.variable === 'commute_time'
                    ? `${transportMapMetrics.californiaAverage.toFixed(1)} min`
                    : `${Math.round(transportMapMetrics.californiaAverage).toLocaleString()}`
                }
                label="California Average"
              />
            </KeyMetrics>
          ) : undefined
        }
        chartContent={
          <div id="transportation-maps-chart">
            <GenericMapTiered
              location={currentLocation}
              onDataLoaded={handleTransportMapDataLoaded}
              allowedVariables={allowedTransportMapVariables}
              enableScopeSelector={true}
            />
          </div>
        }
        onDownloadPNG={() => {
          const container = document.querySelector('#transportation-maps-chart');
          const canvas = container?.querySelector('canvas');
          if (canvas) {
            const link = document.createElement('a');
            link.download = `Transportation_Maps_${currentLocation.replace(/\s+/g, '_')}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
          }
        }}
        overviewContent={
          transportMapMetrics ? (
            <OverviewContent
              title={
                transportMapMetrics.variable === 'ev_adoption'
                  ? 'Electric Vehicle Adoption Map Overview'
                  : transportMapMetrics.variable === 'total_chargers'
                  ? 'EV Charging Infrastructure Map Overview'
                  : transportMapMetrics.variable === 'vehicles_per_household'
                  ? 'Vehicles per Household Map Overview'
                  : transportMapMetrics.variable === 'zero_vehicle_households'
                  ? 'Zero Vehicle Households Map Overview'
                  : 'Commute Time Map Overview'
              }
              description={`This map visualizes transportation-related patterns across census tracts in ${currentLocation}, helping identify spatial variation in key indicators.`}
            >
              {transportMapMetrics.variable === 'ev_adoption' ? [
                `${currentLocation} has ${transportMapMetrics.currentValue.toFixed(1)} electric vehicles per 100 people, which is ${((transportMapMetrics.currentValue / transportMapMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${transportMapMetrics.californiaAverage.toFixed(1)} EVs per 100 people.`,
                'Tracts with higher EV adoption often coincide with higher incomes, access to home charging, and early adoption of clean technologies.'
              ] : transportMapMetrics.variable === 'total_chargers' ? [
                `${currentLocation} has ${Math.round(transportMapMetrics.currentValue).toLocaleString()} total EV chargers, compared to a statewide average of ${Math.round(transportMapMetrics.californiaAverage).toLocaleString()} chargers.`,
                'Concentrations of chargers highlight areas with better EV accessibility, while gaps in coverage can indicate equity and infrastructure planning needs.'
              ] : transportMapMetrics.variable === 'vehicles_per_household' ? [
                `${currentLocation} has ${transportMapMetrics.currentValue.toFixed(2)} vehicles per household, which is ${((transportMapMetrics.currentValue / transportMapMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${transportMapMetrics.californiaAverage.toFixed(2)} vehicles per household.`,
                'Higher vehicles per household generally signal greater auto dependence and higher transportation emissions per household.'
              ] : transportMapMetrics.variable === 'zero_vehicle_households' ? [
                `Estimated zero-vehicle households in ${currentLocation} total ${Math.round(transportMapMetrics.currentValue).toLocaleString()}, compared to a statewide average of ${Math.round(transportMapMetrics.californiaAverage).toLocaleString()}.`,
                'Tracts with higher shares of zero-vehicle households are especially sensitive to transit service quality and walkability.'
              ] : [
                `${currentLocation} has an average commute time of ${transportMapMetrics.currentValue.toFixed(1)} minutes, which is ${((transportMapMetrics.currentValue / transportMapMetrics.californiaAverage) * 100).toFixed(1)}% of the California average of ${transportMapMetrics.californiaAverage.toFixed(1)} minutes.`,
                'Longer commute times can indicate jobs-housing imbalances, limited transit options, or long-distance driving patterns.'
              ]}
            </OverviewContent>
          ) : undefined
        }
        relevanceContent={
          <RelevanceContent
            title="Relevance to Climate Action"
            description="Transportation-related maps help identify spatial patterns in vehicle dependence, EV readiness, and access to alternatives."
          >
            <div className="methodology-details-simple">
              <h5>Targeting Clean Transportation Investments</h5>
              <p>Maps of EV adoption, charging infrastructure, and vehicle access can guide where to prioritize transit, EV incentives, and active transportation projects.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Reducing VMT and Emissions</h5>
              <p>Areas with high vehicle ownership and long commutes are prime candidates for transit-oriented development and mode shift strategies.</p>
            </div>
          </RelevanceContent>
        }
        equityContent={
          <EquityContent
            title="Equity Considerations"
            description="Spatial patterns in transportation access are closely tied to income, race, and housing tenure."
          >
            <div className="methodology-details-simple">
              <h5>Access to Clean Mobility</h5>
              <p>Neighborhoods with low EV adoption and limited charging may require targeted programs to ensure equitable access to clean transportation options.</p>
            </div>
            <div className="methodology-details-simple">
              <h5>Transit and Walkability Gaps</h5>
              <p>Areas with many zero-vehicle households but poor transit or pedestrian infrastructure face disproportionate mobility challenges.</p>
            </div>
          </EquityContent>
        }
        methodologyContent={
          <MethodologyContent
            title="Methodology"
            description="Transportation map layers are based on tract-level datasets joined to cities and counties using correspondence tables."
            dataSources={[
              'ev_by_tract_2023 (EV adoption, charging infrastructure, vehicles per household, zero-vehicle households, population, commute time)',
              'Population and housing data from the American Community Survey 5-Year Estimates',
              'Regions table for 10-region grouping used in the Multifamily Energy Efficiency Targeting Tool'
            ]}
            methodology={[
              'Tract-level indicators are aggregated and summarized for the selected location and scope (county, region, or California).',
              'EV adoption and charging infrastructure metrics are derived from registration and station datasets joined to census tracts.',
              'Zero-vehicle households are estimated from percentages and average household size, using tract-level population.',
              'Commute time data comes from ACS travel-to-work tables and is averaged across tracts in the selected geography.'
            ]}
          />
        }
        activeView={activeTransportMapView}
        onViewChange={(view: string) =>
          setActiveTransportMapView(view as 'overview' | 'relevance' | 'equity' | 'methodology')
        }
        showRelevanceEquity={true}
        enableComments={true}
        location={currentLocation}
        registerPng={false}
      />
      </div>
      </div>

      <CustomSectionsForPage pageId="transportation" locationName={currentLocation} />
      <CustomDatasetsForPage pageId="transportation" locationName={currentLocation} />
    </div>
  );
};
